<template>
  <div id="email_campaigns_performance">
    <ContainerInfoItem
      source="email_campaigns_performance"
      title="Campaigns Performance"
      :description="range.label"
      height="fit-content"
    >
      <SkeletonLoader v-if="isLoading" :page="loaderPages.TABLE" />

      <CustomTable
        v-else-if="allTableData?.length > 0"
        :headers="emailCampaignsPerformanceTableHeaders"
        :data="paginatedData"
        :show-page-size="false"
        :pagination="pagination"
        @update:pagination="handlePageChange"
        class="mt-4"
      />

      <EmptyScreen
        v-else
        source="email_campaigns_performance"
        text="No data found!"
        :type="emptyScreenTypes.CHART_AND_TABLE"
      />
    </ContainerInfoItem>
  </div>
</template>

<script setup>
import { computed, ref, watchEffect } from "vue";
import store from "@/src/store/index.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import { emailCampaignsPerformanceTableHeaders } from "@/src/constants/analyticConstants.js";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import { emptyScreenTypes } from "@/src/constants/searchConstants.js";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const range = computed(() => store.getters["analytics/getRange"]);
const filters = computed(() => store.getters["analytics/getFilters"]);
const allTableData = ref([]);
const isLoading = ref(false);
const pagination = ref({
  current: 1,
  pageSize: 5,
  total: 0,
});

const paginatedData = computed(() => {
  if (!allTableData.value) return [];

  const start = (pagination.value.current - 1) * pagination.value.pageSize;
  const end = start + pagination.value.pageSize;

  return allTableData.value.slice(start, end);
});

const handlePageChange = (newPagination) => {
  pagination.value = { ...pagination.value, current: newPagination.current };
};

const toFetchEmailCampaignPerformance = async () => {
  try {
    isLoading.value = true;

    const params = {
      email_id: filters.value?.mailbox?.id,
      start_time: range.value?.start,
      end_time: range.value?.end,
    };

    const response =
      await AnalyticService.V2.fetchEmailCampaignPerformance(params);

    if (response?.success && response.data?.data) {
      const data = response.data?.data;
      allTableData.value = data?.data || [];
      pagination.value.total = data?.total_output;
    } else {
      allTableData.value = [];
      pagination.value.total = 0;
    }
  } catch (error) {
    console.error(
      "An error occurred while fetching email campaign performance: ",
      error,
    );
    allTableData.value = [];
    pagination.value.total = 0;
  } finally {
    isLoading.value = false;
  }
};

const resetData = () => {
  allTableData.value = [];
  pagination.value = {
    current: 1,
    pageSize: 5,
    total: 0,
  };
};

watchEffect(() => {
  if (range.value) {
    resetData();
    toFetchEmailCampaignPerformance();
  }
});

useAnalyticsRefresh(() => {
  resetData();
  toFetchEmailCampaignPerformance();
}, [range]);
</script>
